import { inject, Injectable } from '@angular/core';
import { BookableStorageLocation, Booking, LatLng } from '@luggagehero/shared/interfaces';
import { SharedGeolocationService } from '@luggagehero/shared/services/geolocation';
import { SharedStorageCriteriaService } from '@luggagehero/shared/services/storage-criteria';
import { SharedUtilGeo } from '@luggagehero/shared/util';

import { SharedAnalyticsService } from './shared-analytics.service';

/**
 * Sent once per tap on a link that opens a maps app from a booking (AM-4927). It is the first step of
 * the navigate → arrive → drop-off funnel, so it is logged as an app event (`events.app-events`) and
 * not only to GTM: the existing `ViewGoogleMapsBooking` angulartics event on the same links never
 * reaches the app-events store.
 */
export const DIRECTIONS_OPENED_EVENT = 'directions_opened';

/**
 * Where the tap happened, named after the component that renders the link:
 * - `go-to-shop` — the "find your storage location" sheet. The booking timeline has no maps link of
 *   its own; both of its "Get directions" entry points open this sheet, so a timeline tap lands here.
 * - `manage-booking` — the legacy (schema-v1) manage-booking page's "find shop" modal.
 * - `booking-info` — the address row of the booking details on the legacy manage-booking page.
 * - `booking-home` — reserved for the booking home (AM-4923); nothing sends it yet.
 */
export type DirectionsSurface = 'go-to-shop' | 'manage-booking' | 'booking-info' | 'booking-home';

/**
 * What the link opens: `directions` is a route to the location, `place` is the location's pin. Both
 * leave the app for maps, so both are counted; filter on this to count route requests only.
 */
export type DirectionsLinkType = 'directions' | 'place';

/**
 * Where the traveler position used for `distanceMeters` came from:
 * - `device` — a device position fix taken in this session within {@link DEVICE_POSITION_MAX_AGE_MS}.
 *   Web only: on native, `NativeGeolocationService` returns its Capacitor fix without caching it in
 *   `currentLocation`, so native events carry `search` or `null`. Compare distances within a platform.
 * - `search` — the "my location" search the traveler last ran; it carries no timestamp, so it may be old.
 */
export type DirectionsDistanceSource = 'device' | 'search';

/** Params carried under the app event's `customProperties`. */
export interface DirectionsOpenedEventParams {
  /**
   * Kept here rather than in the top-level `orderId` (which the api maps to `bookingId`), because
   * `orderId` is also forwarded to GTM as a transaction id and this event is not a purchase.
   */
  bookingId: string;
  surface: DirectionsSurface;
  linkType: DirectionsLinkType;
  /** Straight-line distance from the traveler to the location, in whole metres, or `null` when unknown. */
  distanceMeters: number | null;
  distanceSource: DirectionsDistanceSource | null;
}

/** A device fix older than this is too stale to say how far the traveler is from the location. */
export const DEVICE_POSITION_MAX_AGE_MS = 15 * 60 * 1000;

@Injectable({
  providedIn: 'root',
})
export class SharedDirectionsAnalyticsService {
  private analytics = inject(SharedAnalyticsService);
  private geolocationService = inject(SharedGeolocationService);
  private criteriaService = inject(SharedStorageCriteriaService);

  /**
   * Call from the link's click handler. Never asks for, or waits on, a position: the tap is about to
   * leave the app, so the event goes out now with whatever position is already known.
   */
  public directionsOpened(
    booking: Booking | null | undefined,
    shop: BookableStorageLocation | null | undefined,
    surface: DirectionsSurface,
    linkType: DirectionsLinkType,
  ): void {
    try {
      const storageLocationId = booking?.shopId || shop?._id;
      if (!booking?._id || !storageLocationId) {
        return;
      }

      const params: DirectionsOpenedEventParams = {
        bookingId: booking._id,
        surface,
        linkType,
        ...this.distanceTo(shop),
      };

      this.analytics.track(DIRECTIONS_OPENED_EVENT, {
        category: 'directions',
        label: surface,
        storageLocationId,
        customProperties: params,
      });
    } catch {
      // Analytics must never break the link it is attached to
    }
  }

  private distanceTo(
    shop: BookableStorageLocation | null | undefined,
  ): Pick<DirectionsOpenedEventParams, 'distanceMeters' | 'distanceSource'> {
    const unknown = { distanceMeters: null, distanceSource: null };

    const coordinates = shop?.location?.coordinates;
    if (!coordinates || !Number.isFinite(coordinates[0]) || !Number.isFinite(coordinates[1])) {
      return unknown;
    }
    // GeoJSON order: [lng, lat]
    const shopLocation: LatLng = { lat: coordinates[1], lng: coordinates[0] };

    const travelerPosition = this.travelerPosition();
    if (!travelerPosition) {
      return unknown;
    }

    const km = SharedUtilGeo.getDistance(travelerPosition.location, shopLocation);
    if (!Number.isFinite(km)) {
      return unknown;
    }
    return { distanceMeters: Math.round(km * 1000), distanceSource: travelerPosition.source };
  }

  private travelerPosition(): { location: LatLng; source: DirectionsDistanceSource } | null {
    const device = this.geolocationService.currentLocation;
    if (device?.location && Date.now() - new Date(device.updatedAt).getTime() <= DEVICE_POSITION_MAX_AGE_MS) {
      return { location: device.location, source: 'device' };
    }

    const search = this.criteriaService.location;
    if (search && search.type === 'user' && Number.isFinite(search.lat) && Number.isFinite(search.lon)) {
      return { location: { lat: search.lat, lng: search.lon }, source: 'search' };
    }

    return null;
  }
}
